// src/components/Auth/Login.js
import React, { useState, useEffect } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../../firebase/config";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./Auth.css";

const API_URL = process.env.REACT_APP_API_URL || 'http://http://https://southamerica-east1-drecenna.cloudfunctions.net/api';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Verificar se temos uma mensagem de sucesso da página de criação de conta
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
    }
    
    // Aplicar dark theme ao root para garantir que o tema se aplique a tudo
    document.documentElement.classList.add('dark-theme');
    
    // Aplicar estilo inline para garantir que os inputs tenham o mesmo tamanho
    const styles = `
      .input-container {
        width: 100%;
      }
      .input-with-icon {
        width: 100%;
        box-sizing: border-box;
      }
      .password-container {
        position: relative;
      }
      .password-toggle {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        padding: 0;
        width: 24px;
        height: 24px;
        z-index: 1;
      }
    `;
    
    // Adicionar estilos ao head
    const styleElement = document.createElement('style');
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);
    
    // Cleanup function
    return () => {
      document.documentElement.classList.remove('dark-theme');
      document.head.removeChild(styleElement);
    };
  }, [location]);

  const handleCheckSubscription = async () => {
    if (!email) {
      setError("Por favor, informe seu email.");
      return;
    }

    try {
      setCheckingSubscription(true);
      setError("");
      
      // Primeiro, tentar o endpoint normal de recuperação
      const response = await axios.post(`${API_URL}/recover-subscription`, { email });
      
      // Se não encontrou nada, tentar uma busca mais profunda
      if (!response.data.found) {
        setMessage("Buscando informações no Stripe, isso pode levar alguns segundos...");
        
        // Consulta avançada no Stripe
        const stripeResponse = await axios.post(`${API_URL}/check-payments-by-email`, { email });
        
        if (stripeResponse.data.found) {
          // Se encontrou no Stripe, redirecionar com o sessionId
          navigate(`/signup-success?session_id=${stripeResponse.data.sessionId || 'manual-recovery'}`);
          return;
        }
        
        // Se não encontrou nem no Stripe
        setError(
          "Não encontramos nenhuma assinatura para este email. " +
          "Verifique se digitou o mesmo email usado durante o pagamento ou use a recuperação manual."
        );
      } else if (response.data.alreadyRegistered) {
        // Caso especial: já existe conta registrada
        setError("Este email já possui uma conta ativa. Por favor, faça login com sua senha.");
      } else {
        // Se encontrou, redirecionar para a página de conclusão de cadastro
        navigate(`/signup-success?session_id=${response.data.sessionId}`);
      }
    } catch (error) {
      console.error("Erro ao verificar assinatura:", error);
      setError("Ocorreu um erro ao verificar sua assinatura. Por favor, tente novamente ou entre em contato com o suporte.");
    } finally {
      setCheckingSubscription(false);
      setMessage("");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError("Por favor, preencha todos os campos.");
      return;
    }
    
    try {
      setError("");
      setLoading(true);
      
      // Tentar fazer login
      await signInWithEmailAndPassword(auth, email, password);
      
      // Se bem-sucedido, redirecionar para o dashboard
      navigate("/dashboard");
    } catch (error) {
      console.error("Erro ao fazer login:", error);
      
      // Se o erro for "usuário não encontrado", verificar se há uma assinatura pendente
      if (error.code === "auth/user-not-found") {
        try {
          // Verificar se existe uma assinatura pendente para este email
          const response = await axios.post(`${API_URL}/recover-subscription`, { email });
          
          if (response.data.found) {
            setError(
              "Este email possui uma assinatura pendente. Você pode completar seu cadastro clicando no botão abaixo."
            );
            return;
          }
        } catch (checkError) {
          console.error("Erro ao verificar conta pendente:", checkError);
        }
      }
      
      setError(
        error.code === "auth/user-not-found" || error.code === "auth/wrong-password"
          ? "Email ou senha incorretos."
          : "Falha ao fazer login. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <div className="auth-container dark-theme">
      {/* Formas decorativas para o background */}
      <div className="shape shape-1"></div>
      <div className="shape shape-2"></div>
      <div className="shape shape-3"></div>
      
      <div className="auth-card login-card">
        <div className="auth-logo">
          <div className="logo-icon">
            {/* Usando um ícone personalizado da pasta public */}
            <img src="/logo-icon.png" alt="Logo" className="custom-logo-image" />
          </div>
          <h2>Login</h2>
        </div>
        
        {message && <div className="success-message animate-fade-in">{message}</div>}
        {error && <div className="error-message animate-fade-in">{error}</div>}
        
        <form onSubmit={handleLogin} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">
              <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              Email
            </label>
            <div className="input-container">
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu email"
                disabled={loading || checkingSubscription}
                className="input-with-icon"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="password">
              <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              Senha
            </label>
            <div className="input-container password-container">
              <input
                type={passwordVisible ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sua senha"
                disabled={loading || checkingSubscription}
                className="input-with-icon"
              />
              <button 
                type="button" 
                className="password-toggle" 
                onClick={togglePasswordVisibility}
                tabIndex="-1"
              >
                {passwordVisible ? (
                  // Quando a senha está visível, mostrar o ícone de olho aberto
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                ) : (
                  // Quando a senha está oculta, mostrar o ícone de olho fechado/riscado
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          <div className="forgot-password-link">
            <Link to="/forgot-password">Esqueci minha senha</Link>
          </div>
          
          <button 
            type="submit" 
            className="login-button"
            disabled={loading || checkingSubscription}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                <span>Entrando...</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                  <polyline points="10 17 15 12 10 7"></polyline>
                  <line x1="15" y1="12" x2="3" y2="12"></line>
                </svg>
                <span>Entrar</span>
              </>
            )}
          </button>
          
          {error && error.includes("assinatura pendente") && (
            <button 
              type="button" 
              className="secondary-button subscription-recovery-button" 
              onClick={handleCheckSubscription}
              disabled={checkingSubscription}
            >
              {checkingSubscription ? (
                <>
                  <span className="spinner"></span>
                  <span>Verificando...</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                  </svg>
                  <span>Completar Cadastro</span>
                </>
              )}
            </button>
          )}
        </form>
        
        <div className="auth-links">
          <p className="auth-link">
            Não tem uma conta? <Link to="/" className="signup-link">Escolha um plano e assine</Link>
          </p>
          <p className="auth-link subscription-link">
            <Link to="/recover-subscription">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              Já fez o pagamento mas não completou o cadastro?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;